import { InvalidateQueryFilters, QueryClient } from "react-query";
import { QueryParams } from "./types/base";
import { InstitutionRankingListQueryParams } from "./types/institution-ranking";
import { CountryStateListQueryParams } from "./types/enum";
import { InstitutionListQueryParams } from "./types/institution";
import { PlacementEnvironmentTemplateListQueryParams } from "./types/placement-environment-template";
import { LinkSectionEnum } from "./types/link";
import { ExtendedQueryParams } from "./queries/people/usePeopleList";
import { TagQueryParams } from "./queries/tag/list";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
    mutations: {},
  },
});

export const queryKeys = {
  status: () => ["status"],
  //
  userInfo: () => ["user-info"],
  user: (id: string | undefined) => ["user", id],
  userContacts: () => ["user-contacts"],
  //
  userList: () => ["users"],
  userListPage: (params: QueryParams) => ["users", params],
  //
  institution: (id: string | undefined) => ["institution", id],
  institutionList: () => ["institutions"],
  institutionListPage: (params: InstitutionListQueryParams) => [
    "institutions",
    params,
  ],
  institutionListSearch: (params: InstitutionListQueryParams) => [
    "institutions-search",
    params,
  ],
  //
  institutionRanking: (id: string | undefined) => ["institution-ranking", id],
  institutionRankingList: () => ["institution-rankings"],
  institutionRankingListPage: (params: InstitutionRankingListQueryParams) => [
    "institution-rankings",
    params,
  ],
  institutionRankingUser: () => ["institution-ranking-user"],
  //
  checklist: (id: string | undefined) => ["checklist", id],
  checklistList: (
    params: { limit?: number; offset?: number; source?: string } = {},
  ) => [
    "checklists",
    params.limit ?? null,
    params.offset ?? null,
    params.source ?? null,
  ],

  checklistListPage: (params: QueryParams) => ["checklists", params],
  //
  representative: (id: string | undefined) => ["representative", id],
  representativeList: () => ["representatives"],
  representativeListPage: (params: QueryParams) => ["representatives", params],
  representativesSearch: (query: string) => [
    "representatives",
    "search",
    query,
  ],
  //
  placementEnvironment: (id: string | undefined) => [
    "placement-environment",
    id,
  ],
  placementEnvironmentList: () => ["placement-environments"],
  placementEnvironmentListPage: (params: QueryParams) => [
    "placement-environments",
    params,
  ],
  //
  placementEnvironmentTemplate: (id: string | undefined) => [
    "placement-environment-template",
    id,
  ],
  placementEnvironmentTemplateList: () => ["placement-environment-templates"],
  placementEnvironmentTemplateListPage: (
    params: PlacementEnvironmentTemplateListQueryParams,
  ) => ["placement-environment-templates", params],
  //
  interaction: (id: string | undefined) => ["interaction", id],
  interactionList: () => ["interactions"],
  interactionListPage: (params: QueryParams) => ["interactions", params],
  interactionTypes: () => ["interaction-types"],
  interactionTypesSearch: (query: string) => [
    "interaction-types",
    "search",
    query,
  ],
  interactionUsers: () => ["interaction-users"],
  //
  userChecklist: (id: string | undefined) => ["user-checklist", id],
  userChecklists: () => ["user-checklists"],
  //
  countries: () => ["countries"],
  countryStates: (params: CountryStateListQueryParams) => [
    "country-states",
    params,
  ],
  journeyTypes: () => ["journey-types"],
  contactPlatforms: () => ["contact-platforms"],
  skills: () => ["skills"],
  campusActivities: () => ["campus-activities"],
  fields: () => ["fields"],
  //
  people: (id: string | undefined) => ["people", id],
  peopleList: (params?: ExtendedQueryParams) => {
    if (!params) return ["people-list"];
    return [
      "people-list",
      params.approved,
      params.offset,
      params.limit,
      params.search || "",
      params.sortField,
      params.sortOrder,
    ];
  },
  peopleListPage: (params: QueryParams) => ["people-list", params],
  peopleAccountVisibility: (id: string | undefined) => [
    "people-account-visibility",
    id,
  ],
  //
  account: (id: string | undefined) => ["account", id],
  accountList: () => ["account-list"],
  accountListPage: (params: QueryParams) => ["account-list", params],
  //
  googleSearch: (query: string) => ["google-search", query],
  //
  links: (section: LinkSectionEnum, id: string | undefined) => [
    "links",
    section,
    id,
  ],
  tag: (id: string | undefined) => ["tag", id],
  tagList: (params?: TagQueryParams) => {
    if (!params) return ["tag-list"];
    return ["tag-list", params.search || ""];
  },
  //
  fieldType: (id: string | undefined) => ["field-type", id],
  fieldTypes: (params?: { page?: number; limit?: number; search?: string }) => {
    if (!params) return ["field-types"];
    return ["field-types", params.page, params.limit, params.search];
  },
  fieldTypeSearch: (params: { q: string; limit?: number }) => [
    "field-types-search",
    params,
  ],
  fieldTypeSearchGroups: (params: { q: string }) => [
    "field-types-search-groups",
    params,
  ],
  //
  profileFields: (profileId: string | undefined) => [
    "profile-fields",
    profileId,
  ],
  fieldTypesByNames: (names: string[]) => [
    "field-types-by-names",
    names.sort(), // Sort for consistent cache keys
  ],
  allFieldTypes: (active: boolean = true) => ["field-types-all", active],
  tallyFormQuestions: (formId?: string | null) => [
    "tally-form-questions",
    formId ?? null,
  ],
  fieldTypesFlat: (search?: string) => ["field-types-flat", search ?? null],
  tallyForm: (id: string | undefined) => ["tally-form", id],
  tallyForms: () => ["tally-forms"],
};

export const defaultInvalidateQueryFilters: InvalidateQueryFilters = {
  refetchInactive: true,
};
